/**
 * Type pour les paramètres combinés (URL params + route params)
 */
export interface MockRouteParams {
  queryParams: Record<string, string | number>; // Query params de l'URL
  routeParams: Record<string, string | number>; // Params extraits du pattern
}

/**
 * Type pour le handler de route
 */
export type MockRouteHandler<T> = (
  params: MockRouteParams,
  payload?: any
) => T | T[] | { data: T[]; total: number; page: number; limit: number };

/**
 * Configuration d'une route mock
 */
export interface MockRoute<T> {
  pattern: string;
  handler: MockRouteHandler<T>;
}

export abstract class MpsMockBaseService<T extends { id: any }> {
  constructor() {}

  public abstract generateOne(): T;
  public abstract getBaseRoute(): string;

  /**
   * Définit les routes personnalisées pour ce service
   * Chaque route associe un pattern à un handler
   * Le pattern peut inclure la méthode HTTP au format "METHOD::pattern"
   *
   * @example
   * ```typescript
   * protected getRoutes(): Record<string, MockRouteHandler<Zouma>> {
   *   return {
   *     'GET::/zoumas/{id}': (params) => this.get(params.routeParams.id),
   *     'GET::/zoumas': (params) => this.all(),
   *     'POST::/zoumas': (params, payload) => this.add(payload),
   *     'PUT::/zoumas/{id}': (params, payload) => this.update(params.routeParams.id, payload),
   *     'DELETE::/zoumas/{id}': (params) => this.delete(params.routeParams.id),
   *     '/zoumas/special': (params) => this.all(), // Sans méthode = accepte toutes les méthodes
   *   };
   * }
   * ```
   */
  protected abstract getRoutes(): Record<string, MockRouteHandler<T>>;

  public generateMany(count: number): T[] {
    const items: T[] = [];
    for (let i = 0; i < count; i++) {
      const item = this.generateOne();
      item.id = i + 1;
      items.push(item);
    }
    return items;
  }
}
